import { Button, ConfigProvider, Flex, Slider } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { PauseIcon, PlayIcon } from "@phosphor-icons/react";
import { useAntTheme } from "hooks/useAntTheme/";
import { useEffect, useRef, useState } from "react";
import { VoiceProfileResponse } from "../../types";
import { getVoicePreviewForLanguage } from "./helpers";
import { useVoicePreviewGeneration } from "../../hooks/useVoicePreviewGeneration";

interface SliderVoicePreviewProps {
  voice: VoiceProfileResponse;
  style?: React.CSSProperties;
  sliderWidth?: number;
  currentlyPlayedVoice?: VoiceProfileResponse;
  onClickPlay?: (voice?: VoiceProfileResponse) => void;
  language?: string;
  /**
   * When provided, enables on-demand preview generation: if the voice has no
   * preview asset for the target language, clicking play requests one and the
   * audio autoplays once the new path lands.
   */
  organizationId?: string;
}

export const SliderVoicePreview = ({
  voice,
  currentlyPlayedVoice,
  onClickPlay,
  style,
  sliderWidth,
  language,
  organizationId,
}: SliderVoicePreviewProps) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { theme } = useAntTheme();
  const isPlaying = currentlyPlayedVoice?.id === voice.id;

  const audioPath = getVoicePreviewForLanguage(voice, language);
  const targetLanguage = language ?? voice.original_language?.language;

  // Set when the user clicks play before audio is ready (on-demand generation).
  // Consumed by handleLoadedMetadata to autoplay once the new audio loads.
  const pendingAutoPlayRef = useRef(false);

  const { requestPreview, isPending } = useVoicePreviewGeneration({
    voiceProfileId: voice.id,
    organizationId,
    currentPreviewPath: audioPath,
  });

  const handleGeneratePreview = () => {
    if (!targetLanguage) return;
    pendingAutoPlayRef.current = true;
    requestPreview(targetLanguage).catch((error) => {
      console.error("Error generating preview:", error);
    });
  };

  useEffect(() => {
    if (!audioRef.current) return;
    if (!isPlaying) {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      if (!Number.isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
      if (pendingAutoPlayRef.current) {
        pendingAutoPlayRef.current = false;
        audio.play().then(
          () => onClickPlay?.(voice),
          (error) => console.error("Error autoplaying audio:", error)
        );
      }
    };

    const handleEnded = () => {
      setCurrentTime(0);
      onClickPlay?.();
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    if (audio.duration && !Number.isNaN(audio.duration)) {
      setDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioPath, onClickPlay, voice]);

  const handlePlayPause = async () => {
    if (!audioPath) {
      handleGeneratePreview();
      return;
    }
    onClickPlay?.(voice);
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      onClickPlay?.();
    } else {
      if (audioRef.current.currentTime === audioRef.current.duration) {
        audioRef.current.currentTime = 0;
        setCurrentTime(0);
      }
      try {
        await audioRef.current.play();
        onClickPlay?.(voice);
      } catch (error) {
        console.error("Error playing audio:", error);
      }
    }
  };

  const handleSeek = (value: number) => {
    if (!audioRef.current) return;
    const time = (value / 100) * duration;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  // Without organizationId, on-demand generation isn't available, so a voice
  // with no preview asset can't render anything useful.
  if (!audioPath && !organizationId) {
    return null;
  }

  // No way to play: no existing audio and no language to generate one for.
  const disabled = !audioPath && !targetLanguage;

  let playIcon: React.ReactNode;
  if (isPending) {
    playIcon = <LoadingOutlined style={{ fontSize: 20 }} spin />;
  } else if (isPlaying) {
    playIcon = <PauseIcon size={20} weight="fill" />;
  } else {
    playIcon = <PlayIcon size={20} weight="fill" />;
  }

  return (
    <>
      <Flex
        align="center"
        gap={8}
        style={{ width: "100%", ...style }}
        onClick={(e) => e.stopPropagation()}
        data-testid="slider-voice-preview-flex"
      >
        <Button
          type="text"
          shape="circle"
          disabled={disabled || isPending}
          onClick={() => {
            void handlePlayPause();
          }}
          icon={playIcon}
        />
        <ConfigProvider
          theme={{
            components: {
              Slider: {
                handleSize: 0,
                railSize: 4,
                handleSizeHover: 0,
                handleLineWidth: 0,
                handleLineWidthHover: 0,
                trackBg: theme.colorBorder,
                trackHoverBg: theme.colorBorder,
                railBg: theme.colorFillSecondary,
                handleColor: "transparent",
                handleActiveColor: "transparent",
                handleActiveOutlineColor: "transparent",
                dotActiveBorderColor: "transparent",
              },
            },
          }}
        >
          <Slider
            value={duration > 0 ? (currentTime / duration) * 100 : 0}
            onChange={handleSeek}
            tooltip={{ formatter: null }}
            style={{ width: sliderWidth ?? "100%" }}
            min={0}
            max={100}
            step={0.1}
          />
        </ConfigProvider>
      </Flex>
      {audioPath && (
        <audio ref={audioRef} src={audioPath}>
          <track kind="captions" src="" />
        </audio>
      )}
    </>
  );
};
